/**
 * @file legal-slugs.ts
 * @description Адреса юридических документов магазина (phase19.16, партия
 * «Юридические документы» SB139–SB144; проект данных
 * docs/phases/phase19.16-materials/db-design-legal-docs.md §1.1, §4, §5, §6.2).
 *
 * Зачем в shared: обязательные документы узнаются по адресу (решение
 * проекта §1.1 — отдельной колонки «роль» нет), а адреса страниц, редакций и
 * файлов строят API (site-config, меню, письма, «Оферта, ред. N»), витрина
 * (маршруты `/legal/…`, карта сайта) и кабинет (поле «Адрес», заготовки).
 * Раньше константы жили в четырёх копиях (`SITE_LEGAL_DOC_SLUGS` в api,
 * `LEGAL_ALIASES` в web, заготовки admin) — теперь источник один.
 *
 * Правила:
 *   - адрес: латиница в нижнем регистре, цифры и одиночные дефисы, 1–60;
 *   - `offer` → `/offer`, `privacy-policy` → `/privacy` (жёсткие
 *     переадресации витрины), остальные документы и группы — `/legal/<адрес>`;
 *   - «Предыдущие редакции» — всегда `/legal/<адрес>/revisions`, в том числе
 *     у оферты и политики (§5.2: отдельный маршрут, переадресация его не
 *     задевает); страницы редакций открыты (noindex), пока у документа есть
 *     редакции, даже если он снят (решение оркестратора 2026-10-19, вопрос 1);
 *   - файл PDF — через API, не через маршрут витрины (решение оркестратора
 *     2026-10-19 по выводу devops): файл редакции
 *     `/api/v1/public/legal/files/<revisionId>.pdf` (неизменяемый адрес),
 *     действующий файл — `/api/v1/public/legal/<адрес>/file` (302 на файл
 *     действующей редакции);
 *   - оферта и политика ПДн — только вид «Текст» (SB140);
 *   - согласие на обработку ПДн — документ `personal-data-consent`, без него
 *     согласие ссылается на политику (решение 2026-10-19, `LEGAL_PD_CONSENT_SLUGS`).
 * Все адреса относительные: работают на любом домене магазина (phase19.28).
 */

import { deepFreeze } from "../site-theme/deep-freeze.js";
import { LEGAL_SLUG_MAX_LENGTH } from "./legal-limits.js";

/** Обязательные документы магазина, которые узнаются по адресу (SB17, SB140). */
export const LEGAL_SYSTEM_SLUG_LIST = deepFreeze(["offer", "privacy-policy", "returns-notice", "personal-data-consent"] as const);
export type LegalSystemSlug = (typeof LEGAL_SYSTEM_SLUG_LIST)[number];

/** Ссылки платформы нижней полосы подвала, за которыми стоит документ (SB17). */
export type LegalFooterLinkKind = "offer" | "privacy" | "returns";

/** Сведения об обязательном документе. */
export interface LegalSystemSlugInfo {
  slug: LegalSystemSlug;
  /** Название по-русски — подпись в кабинете и заготовка названия. */
  label: string;
  /** SB140: только вид «Текст» (CHECK `LegalDocument_text_only_slugs_check`). */
  textOnly: boolean;
  /** Ссылка платформы в нижней полосе подвала, которая ведёт на документ. */
  footerLink: LegalFooterLinkKind | null;
}

/** Обязательные документы: подписи, «только текст», ссылка подвала. */
export const LEGAL_SYSTEM_SLUGS: Readonly<Record<LegalSystemSlug, Readonly<LegalSystemSlugInfo>>> = deepFreeze({
  offer: { slug: "offer", label: "Публичная оферта", textOnly: true, footerLink: "offer" },
  "privacy-policy": {
    slug: "privacy-policy",
    label: "Политика обработки персональных данных",
    textOnly: true,
    footerLink: "privacy",
  },
  "returns-notice": { slug: "returns-notice", label: "Условия возврата", textOnly: false, footerLink: "returns" },
  "personal-data-consent": {
    slug: "personal-data-consent",
    label: "Согласие на обработку персональных данных",
    textOnly: false,
    footerLink: null,
  },
});

/** SB140: адреса документов, которые бывают только текстом (оферта и политика ПДн). */
export const LEGAL_TEXT_ONLY_SLUGS: readonly LegalSystemSlug[] = deepFreeze(
  LEGAL_SYSTEM_SLUG_LIST.filter((slug) => LEGAL_SYSTEM_SLUGS[slug].textOnly),
);

/**
 * Адрес документа по ссылке платформы нижней полосы — замена
 * `SITE_LEGAL_DOC_SLUGS` (apps/api site-view-targets.ts).
 */
export const LEGAL_FOOTER_LINK_SLUGS: Readonly<Record<LegalFooterLinkKind, LegalSystemSlug>> = deepFreeze({
  offer: "offer",
  privacy: "privacy-policy",
  returns: "returns-notice",
});

/**
 * Документы, которые ведёт ссылка платформы подвала: в окне «+ Добавить
 * ссылку на документ» кабинет их не предлагает (§6.1) — они уже в полосе.
 */
export const LEGAL_FOOTER_PLATFORM_SLUGS: readonly LegalSystemSlug[] = deepFreeze(
  LEGAL_SYSTEM_SLUG_LIST.filter((slug) => LEGAL_SYSTEM_SLUGS[slug].footerLink !== null),
);

/**
 * Решение 2026-10-19: на какой документ ссылается согласие на обработку
 * ПДн — первый видимый по порядку: отдельное согласие, иначе политика.
 */
export const LEGAL_PD_CONSENT_SLUGS: readonly LegalSystemSlug[] = deepFreeze(["personal-data-consent", "privacy-policy"]);

/** Адрес документа для согласия на обработку ПДн; null — ни один не виден (dev: заглушка PolicyVersion). */
export function legalPdConsentSlug(visibleSlugs: ReadonlySet<string>): LegalSystemSlug | null {
  return LEGAL_PD_CONSENT_SLUGS.find((slug) => visibleSlugs.has(slug)) ?? null;
}

/** Обязательный ли документ (узнаётся по адресу). */
export function isLegalSystemSlug(slug: string): slug is LegalSystemSlug {
  return (LEGAL_SYSTEM_SLUG_LIST as readonly string[]).includes(slug);
}

/** SB140: бывает ли документ с этим адресом только текстом. */
export function isLegalTextOnlySlug(slug: string): boolean {
  return (LEGAL_TEXT_ONLY_SLUGS as readonly string[]).includes(slug);
}

// ── Правила адреса ──────────────────────────────────────────────────────────

/** Латиница в нижнем регистре, цифры, одиночные дефисы без ведущих и конечных (как прежний DTO api). */
export const LEGAL_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Подсказка к правилу адреса — текст ошибки DTO и подсказка поля в кабинете. */
export const LEGAL_SLUG_RULE_MESSAGE =
  "Адрес — латиница в нижнем регистре, цифры и дефисы (без дефиса в начале и в конце), до 60 знаков.";

/**
 * Адреса, которые нельзя занять документом:
 *   - `files`, `certificate-files` — сегменты публичного API
 *     (`/public/legal/files/<id>.pdf`, `/public/legal/certificate-files/<id>`),
 *     с ними `/public/legal/:slug` пересёкся бы (§4, §5.1);
 *   - `privacy` — путался бы с `/privacy` (политика ПДн);
 *   - `legal`, `revisions`, `file` — служебные слова маршрутов (`/legal`,
 *     `/legal/<адрес>/revisions`, `/public/legal/<адрес>/file`);
 *   - `certificates` — за автоматической группой сертификатов (SB141, §7):
 *     ей адрес разрешён (`isReservedLegalSlug` с `groupSource`).
 */
export const LEGAL_RESERVED_SLUGS = deepFreeze([
  "files",
  "certificate-files",
  "privacy",
  "legal",
  "revisions",
  "file",
  "certificates",
] as const);

/** Адрес и название автоматической группы сертификатов по умолчанию (§7; оба можно сменить). */
export const LEGAL_CERTIFICATES_GROUP_DEFAULTS: Readonly<{ slug: string; title: string }> = deepFreeze({
  slug: "certificates",
  title: "Сертификаты",
});

/** Кто занимает адрес: автоматической группе сертификатов разрешён `certificates`. */
export interface LegalSlugOwner {
  groupSource?: "MANUAL" | "PRODUCER_CERTIFICATES" | null;
}

/** Зарезервирован ли адрес. */
export function isReservedLegalSlug(slug: string, owner: LegalSlugOwner = {}): boolean {
  const value = slug.toLowerCase();
  if (value === LEGAL_CERTIFICATES_GROUP_DEFAULTS.slug && owner.groupSource === "PRODUCER_CERTIFICATES") return false;
  return (LEGAL_RESERVED_SLUGS as readonly string[]).includes(value);
}

/** Почему адрес не подходит. */
export type LegalSlugIssue = "legal_slug_invalid" | "legal_slug_reserved";

/**
 * Проверка адреса при создании документа: формат и длина, затем
 * зарезервированные. null — адрес годится (занятость проверяет сервер по
 * базе — `legal_slug_taken`; «только текст» — `legalKindAllowedForSlug`).
 */
export function legalSlugIssue(slug: string, owner: LegalSlugOwner = {}): LegalSlugIssue | null {
  if (slug.length < 1 || slug.length > LEGAL_SLUG_MAX_LENGTH || !LEGAL_SLUG_PATTERN.test(slug)) {
    return "legal_slug_invalid";
  }
  return isReservedLegalSlug(slug, owner) ? "legal_slug_reserved" : null;
}

/** SB140: можно ли создать документ этого вида с этим адресом (оферта и политика — только TEXT). */
export function legalKindAllowedForSlug(slug: string, kind: "TEXT" | "PDF" | "GROUP"): boolean {
  return kind === "TEXT" || !isLegalTextOnlySlug(slug);
}

// ── Адреса на витрине ───────────────────────────────────────────────────────

/** Страница «Все документы» (SB141). */
export const LEGAL_INDEX_HREF = "/legal";

/** Подпись страницы «Все документы» (своя строка подвала, крошки). */
export const LEGAL_INDEX_LABEL = "Все документы";

/** Адрес оферты и политики на витрине — жёсткие переадресации (SB140). */
const SHORT_HREFS: Readonly<Record<string, string>> = deepFreeze({ offer: "/offer", "privacy-policy": "/privacy" });

/** Номер редакции — целое ≥ 1; иначе ошибка вызывающего (fail fast). */
function revisionNumber(number: number): number {
  if (!Number.isSafeInteger(number) || number < 1) {
    throw new RangeError(`legal_revision_number_invalid: ${String(number)}`);
  }
  return number;
}

/**
 * Адрес документа в сегменте URL: только по правилу адреса (иначе ошибка
 * вызывающего — в базе такого адреса быть не может, DTO его не пропускает);
 * `encodeURIComponent` — вторая линия защиты, если правило когда-нибудь
 * расширят (адрес не должен уводить в чужой путь).
 */
function urlSlug(slug: string): string {
  if (!LEGAL_SLUG_PATTERN.test(slug)) throw new RangeError(`legal_slug_invalid: ${slug}`);
  return encodeURIComponent(slug);
}

/** Адрес страницы документа или группы: оферта — `/offer`, политика — `/privacy`, иначе `/legal/<адрес>`. */
export function legalDocumentHref(slug: string): string {
  return SHORT_HREFS[slug] ?? `${LEGAL_INDEX_HREF}/${urlSlug(slug)}`;
}

/** «Предыдущие редакции» — у оферты и политики тоже под `/legal/…` (§5.2). */
export function legalRevisionsHref(slug: string): string {
  return `${LEGAL_INDEX_HREF}/${urlSlug(slug)}/revisions`;
}

/** Страница редакции N (noindex): «Оферта, ред. N» в ЛК, письмах и карточке заказа. */
export function legalRevisionHref(slug: string, number: number): string {
  return `${legalRevisionsHref(slug)}/${revisionNumber(number)}`;
}

// ── Адреса файлов (через API) ───────────────────────────────────────────────

/** Префикс публичного API документов на домене магазина (`location /api`). */
export const LEGAL_PUBLIC_API_PREFIX = "/api/v1/public/legal";

/**
 * Префикс файлов редакций — строка `Allow:` в robots.txt витрины (SB144:
 * PDF индексируется по ссылке, а `/api/` в robots закрыт).
 */
export const LEGAL_FILES_PATH_PREFIX = `${LEGAL_PUBLIC_API_PREFIX}/files/`;

/** id в сегменте адреса: без `/`, `?`, `#` и пробелов (cuid и подобные). */
const URL_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function urlId(id: string, what: string): string {
  if (!URL_ID_PATTERN.test(id)) throw new RangeError(`legal_${what}_id_invalid: ${id}`);
  return id;
}

/**
 * Файл редакции: `/api/v1/public/legal/files/<revisionId>.pdf` — адрес
 * неизменяемый (immutable-кэш). `download` → `?download=1` (`attachment`);
 * способ показа документа (`pdfDisplay`) на адрес не влияет (§5.1, п. 1).
 */
export function legalFileHref(revisionId: string, options: { download?: boolean } = {}): string {
  const base = `${LEGAL_FILES_PATH_PREFIX}${urlId(revisionId, "revision")}.pdf`;
  return options.download ? `${base}?download=1` : base;
}

/** Действующий файл документа: `/api/v1/public/legal/<адрес>/file` (302 на файл действующей редакции). */
export function legalCurrentFileHref(slug: string): string {
  return `${LEGAL_PUBLIC_API_PREFIX}/${urlSlug(slug)}/file`;
}

/** Файл сертификата автоматической группы (шаг «Сертификаты», §7). */
export function legalCertificateFileHref(certificateId: string): string {
  return `${LEGAL_PUBLIC_API_PREFIX}/certificate-files/${urlId(certificateId, "certificate")}`;
}

// ── Ключи хранилища (бакет `legal`) ─────────────────────────────────────────

/** Логическое имя бакета `StorageBucket` (физически `chaiberi-legal`, versioning, §3.1). */
export const LEGAL_STORAGE_BUCKET = "legal";

/** Ключ нового файла черновика: `shop/<shopId>/legal/<documentId>/draft-<uploadId>.pdf`. */
export function legalDraftFileKey(shopId: string, documentId: string, uploadId: string): string {
  return `shop/${urlId(shopId, "shop")}/legal/${urlId(documentId, "document")}/draft-${urlId(uploadId, "upload")}.pdf`;
}

/** Ключ файла редакции: `shop/<shopId>/legal/<documentId>/rev-<revisionId>.pdf`. Не удаляется никогда. */
export function legalRevisionFileKey(shopId: string, documentId: string, revisionId: string): string {
  return `shop/${urlId(shopId, "shop")}/legal/${urlId(documentId, "document")}/rev-${urlId(revisionId, "revision")}.pdf`;
}

/** Ключ черновика: ровно форма `legalDraftFileKey`, без лишних сегментов. */
const DRAFT_KEY_PATTERN = /^shop\/[A-Za-z0-9_-]{1,64}\/legal\/[A-Za-z0-9_-]{1,64}\/draft-[A-Za-z0-9_-]{1,64}\.pdf$/;

/**
 * Страж удаления (§3.1): удалять разрешено только ключ файла черновика.
 * Файлы редакций `rev-…` код не удаляет никогда (SB143). Если передан
 * `shopId` — ключ ещё и обязан принадлежать этому магазину.
 */
export function isLegalDraftKey(key: string, shopId?: string): boolean {
  if (!DRAFT_KEY_PATTERN.test(key)) return false;
  return shopId === undefined || key.startsWith(`shop/${shopId}/legal/`);
}
